import React, { useState, useEffect, memo, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Upload, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Breadcrumb from '../components/breadcrumb';
import StepProgressBar from '../components/StepProgressBar';
import MultiContractorTradesStep from '../components/jobsite-visits/MultiContractorTradesStep';
import Dialog from '../components/Dialog';
import SelectDropdown from '../components/select';
import StatusPill from '../components/StatusPill';
import { JobsiteDetailsSkeleton } from '../components/Skeleton';
import { JobsiteVisitsService } from '../services/jobsite-visits.service';
import { JobsiteService } from '../services/jobsite.service';
import { ContractorService } from '../services/contractor.service';
import { authService } from '../services/auth.service';
import { useJobsiteData } from '../hooks/useJobsiteData';
import { useEditForm } from '../hooks/useEditForm';
import { useJobsiteVisitFileUpload } from '../hooks/useJobsiteVisitFileUpload';
import {
  JobsiteVisitUpdateDTO,
  CreateJobsiteVisitsTradeDTO,
  JobsiteVisitFilesDTO,
  JobsiteVisitResponse,
  JobTradeVisitDTO,
  JobsiteVisitFile,
  ContractorLocalTradeSelection,
  UnionStatus
} from '../types/jobsite-visits.types';
import { getTodayDateString, isDateInFuture, dateToISOAtNoon, extractDateFromISO, isVisitEditable } from '../utils/dateUtils';
import { getJobsiteCompletionPercentage } from '../utils/jobsiteUtils';

// Helper function to format union type display
const formatUnionType = (unionType: string): string => {
  switch (unionType) {
    case 'union':
      return 'Union';
    case 'clac':
      return 'CLAC';
    case 'merit':
      return 'Merit';
    case 'other':
      return 'Unrepresented';
    default:
      return unionType;
  }
};

// Navigation Component
const Navigation = memo(({ onBack }: { onBack: () => void }) => (
  <div className="mb-6 flex items-center">
    <ArrowLeft
      className="text-gray-600 mr-2 w-5 h-5 cursor-pointer"
      onClick={onBack}
      id="back-button"
    />
    <Breadcrumb title="Edit Jobsite Visit" />
  </div>
));

// Form Data Interface
interface EditVisitFormData {
  // Visit date
  visitDate: string; // ISO string format to match database

  // Basic visit info
  status: 'active' | 'inactive' | 'completed';
  jobCompletionStatus: string;
  markAsComp: boolean;

  // Multi-contractor trades data
  contractorTrades: {
    contractors: ContractorLocalTradeSelection[];
  };

  // Files data
  files: File[];

  // Comments
  comments: string;
  existingCommentId?: string;

  // Existing File Data
  existingFiles: JobsiteVisitFile[];
  deletedFiles: JobsiteVisitFile[];
}

// Form Steps Configuration
const FORM_STEPS = [
  { id: 1, title: 'Jobsite Visit Date', description: 'When did you visit this jobsite?' },
  { id: 2, title: 'Trades Information', description: 'Trade details and contractor assignments' },
  { id: 3, title: 'Files & Documents', description: 'Upload supporting documents and photos' },
  { id: 4, title: 'Status & Completion', description: 'Job completion status and progress' },
  { id: 5, title: 'Review & Submit', description: 'Review all information and submit' }
];

// Main Component
const EditJobsiteVisit: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const jobId = params.id;
  const visitId = params.visitId;

  // State Management
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [processingText, setProcessingText] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [hasEdits, setHasEdits] = useState(false);

  // Existing visit data
  const [existingVisit, setExistingVisit] = useState<JobsiteVisitResponse | null>(null);

  // Contractors data
  const [contractors, setContractors] = useState<any[]>([]);

  // Form Data
  const [formData, setFormData] = useState<EditVisitFormData>({
    visitDate: `${new Date().toISOString().split('T')[0]}T12:00:00.000Z`, // Default to today
    status: 'active',
    jobCompletionStatus: '',
    markAsComp: false,
    contractorTrades: {
      contractors: []
    },
    files: [],
    comments: '',
    existingCommentId: undefined,
    existingFiles: [],
    deletedFiles: []
  });

  const initialFormDataRef = useRef<EditVisitFormData | null>(null);

  useEffect(() => {
    // Only set hasEdits to true if we have an initial reference and the data has actually changed
    if (initialFormDataRef.current) {
      const isEdited = JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current);
      setHasEdits(isEdited);
    }
  }, [formData]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasEdits) {
        toast.warn('You have unsaved changes!');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasEdits]);



  // Custom Hooks
  const { jobsiteData } = useJobsiteData(jobId || '', false);
  const { completionSteps } = useEditForm();

  // File upload hook
  const { uploadFiles } = useJobsiteVisitFileUpload();


  // Fetch contractors with trades
  useEffect(() => {
    const fetchContractorsWithTrades = async () => {
      try {
        const contractorsData = await ContractorService.getContractorWithTrades();
        setContractors(contractorsData);
      } catch (error) {
        console.error('Error fetching contractors with trades:', error);
        toast.error('Failed to load contractors');
      }
    };

    fetchContractorsWithTrades();
  }, []);

  // Load existing visit data
  useEffect(() => {
    const loadExistingVisit = async () => {
      if (!visitId) {
        toast.error('Visit ID is required');
        navigate(-1);
        return;
      }

      try {
        setLoading(true);
        const visitData = await JobsiteVisitsService.getJobsiteVisitById(visitId);

        // Admins can edit visits regardless of age; all other roles have a 60-day limit
        const currentUser = authService.getUser();
        if (currentUser?.role !== 'admin' && !isVisitEditable(visitData.visitDate)) {
          toast.error(
            'This visit cannot be edited. Visits can only be edited within 60 days of the visit date.',
            { autoClose: 8000 }
          );
          navigate(`/jobsite-management/details/${jobId}`);
          return;
        }

        setExistingVisit(visitData);

        // Transform existing trade data to multi-contractor structure
        let transformedContractors: ContractorLocalTradeSelection[] = [];

        const jobTradesData = (visitData as any).jobTrades;
        if (jobTradesData && jobTradesData.jobTrade && jobTradesData.jobTrade.length > 0) {
          const tradesData = jobTradesData.jobTrade;

          // Group by contractor -> local -> trade
          const contractorMap = new Map<string, {
            name: string;
            locals: Map<string, {
              name: string;
              trades: Array<{
                tradeId: string;
                tradeName: string;
                unionType: 'union' | 'clac' | 'merit' | 'other';
                journeymen: number;
                apprentices: number;
              }>;
            }>;
          }>();

          tradesData.forEach((tradeItem: any) => {
            if (tradeItem.tradeContractAssociations && tradeItem.tradeContractAssociations.length > 0) {
              tradeItem.tradeContractAssociations.forEach((assoc: any) => {
                const contractorId = assoc.contractor?.id;
                const contractorName = assoc.contractor?.name || 'Unknown Contractor';
                const localUnionId = assoc.localUnion?.id || 'no-local';
                const localUnionName = assoc.localUnion
                  ? `Local ${assoc.localUnion.unionNumber}`
                  : 'Not specified';
                const tradeId = tradeItem.tradeType?.id;
                const tradeName = tradeItem.tradeType?.name || '';
                const unionType = assoc.unionType || 'union';
                const journeymen = assoc.noOfJourneymen || 0;
                const apprentices = assoc.noOfApperntices || 0;

                if (!contractorId || !tradeId) return;

                // Initialize contractor if not exists
                if (!contractorMap.has(contractorId)) {
                  contractorMap.set(contractorId, {
                    name: contractorName,
                    locals: new Map()
                  });
                }

                const contractor = contractorMap.get(contractorId)!;

                // Initialize local if not exists
                if (!contractor.locals.has(localUnionId)) {
                  contractor.locals.set(localUnionId, {
                    name: localUnionName,
                    trades: []
                  });
                }

                const local = contractor.locals.get(localUnionId)!;

                // Add trade to local
                local.trades.push({
                  tradeId,
                  tradeName,
                  unionType: unionType as 'union' | 'clac' | 'merit' | 'other',
                  journeymen,
                  apprentices
                });
              });
            }
          });

          // Convert Map structure to ContractorLocalTradeSelection array
          transformedContractors = Array.from(contractorMap.entries()).map(([contractorId, contractor]) => ({
            contractorId,
            contractorName: contractor.name,
            locals: Array.from(contractor.locals.entries()).map(([localUnionId, local]) => ({
              localUnionId,
              localUnionName: local.name,
              trades: local.trades
            }))
          }));
        }

        // Prepopulate form with existing data
        const initialData: EditVisitFormData = {
          visitDate: visitData.visitDate || `${new Date().toISOString().split('T')[0]}T12:00:00.000Z`,
          status: visitData.status as 'active' | 'inactive' | 'completed',
          jobCompletionStatus: visitData.jobCompletionStatus?.id || '',
          markAsComp: visitData.markAsComp || false,
          contractorTrades: {
            contractors: transformedContractors
          },
          files: [],
          existingFiles: visitData.jobFiles || [],
          deletedFiles: [],
          comments: visitData.comments?.[0]?.comment || '',
          existingCommentId: visitData.comments?.[0]?.id
        };

        setFormData(initialData);
        // Deep copy to prevent reference mutations
        initialFormDataRef.current = JSON.parse(JSON.stringify(initialData));
      } catch (error) {
        console.error('Error loading visit data:', error);
        toast.error('Failed to load visit data');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    loadExistingVisit();
  }, [visitId, navigate]);


  // Step validation
  const validateCurrentStep = useCallback((): boolean => {
    const newErrors: { [key: string]: string } = {};

    switch (currentStep) {
      case 1: // Visit Date step
        if (!formData.visitDate) {
          newErrors.visitDate = 'Visit date is required';
          toast.error('Visit date is required');
        }
        break;

      case 2: // Trades step
        // Validate that each contractor has at least one local with trades
        if (formData.contractorTrades && formData.contractorTrades.contractors) {
          formData.contractorTrades.contractors.forEach((contractor) => {
            // Check if contractor has no locals
            if (contractor.locals.length === 0) {
              newErrors[`contractor_${contractor.contractorId}_locals`] =
                `Please select at least one local for ${contractor.contractorName}`;
              toast.error(`Please select at least one local for ${contractor.contractorName}`);
            } else {
              // Check each local has at least one trade
              contractor.locals.forEach((local) => {
                if (local.trades.length === 0) {
                  newErrors[`contractor_${contractor.contractorId}_local_${local.localUnionId}_trades`] =
                    `Please select at least one trade for ${local.localUnionName}`;
                  toast.error(`Please select at least one trade for ${local.localUnionName} under ${contractor.contractorName}`);
                }

                // Validate each trade has required fields
                local.trades.forEach((trade) => {
                  // Validate union type is selected
                  if (!trade.unionType) {
                    newErrors[`contractor_${contractor.contractorId}_local_${local.localUnionId}_trade_${trade.tradeId}_unionType`] =
                      `Please select union type for ${trade.tradeName}`;
                    toast.error(`Please select union type for ${trade.tradeName}`);
                  }

                  // Validate at least one of journeymen or apprentices is greater than 0
                  const numJourneymen = Number(trade.journeymen) || 0;
                  const numApprentices = Number(trade.apprentices) || 0;

                  if (numJourneymen === 0 && numApprentices === 0) {
                    newErrors[`contractor_${contractor.contractorId}_local_${local.localUnionId}_trade_${trade.tradeId}_workers`] =
                      `Please enter at least one journeyman or apprentice for ${trade.tradeName}`;
                    toast.error(`Please enter at least one journeyman or apprentice for ${trade.tradeName}`);
                  }
                });
              });
            }
          });
        }
        break;

      case 3: // Files step - Optional but can add basic validation
        // Files are optional for visits, so no required validation
        // But we can validate file count and sizes if files are selected
        if (formData.files.length > 10) {
          newErrors.files = 'Maximum 10 files allowed';
          toast.error('Maximum 10 files allowed');
        }

        // Validate individual file sizes
        const invalidFiles = formData.files.filter(file => file.size > 50 * 1024 * 1024);
        if (invalidFiles.length > 0) {
          newErrors.files = `${invalidFiles.length} file(s) exceed 50MB limit`;
          toast.error(`${invalidFiles.length} file(s) exceed 50MB limit`);
        }
        break;

      case 4: // Status step
        if (!formData.jobCompletionStatus && !formData.markAsComp) {
          newErrors.jobCompletionStatus = 'Job completion status is required';
          toast.error('Job completion status is required');
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [currentStep, formData]);

  // Navigation Functions
  const handleBack = useCallback(() => {
    if (hasEdits) {
      toast.warn('You have made edits, and it has not been saved');
    }
    navigate(-1); // Always navigate back
  }, [hasEdits, navigate]);

  const handleNextStep = useCallback(() => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, FORM_STEPS.length));
    }
  }, [validateCurrentStep]);

  const handlePrevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  }, []);




  // Form Update Functions
  const updateFormData = useCallback((updates: Partial<EditVisitFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);



  // File Handling
  const handleFileUpload = useCallback((newFiles: File[]) => {
    // Check total file count (existing + already selected + new)
    const totalExistingFiles = formData.existingFiles.length + formData.files.length;
    const availableSlots = 10 - totalExistingFiles;

    if (availableSlots <= 0) {
      toast.error('Maximum 10 files allowed. Please remove some files before adding more.');
      return;
    }

    if (newFiles.length > availableSlots) {
      toast.error(`You can only add ${availableSlots} more file(s). You currently have ${totalExistingFiles} file(s) (${formData.existingFiles.length} existing + ${formData.files.length} new).`);
      return;
    }

    // Validate file types and sizes
    const validFiles = newFiles.filter(file => {
      const validTypes = ['.pdf', '.png', '.jpeg', '.jpg', '.docx', '.xlsx', '.heif', '.bmp', '.tiff', '.tif', '.xls', '.doc'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const isValidType = validTypes.includes(fileExtension);
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB

      // Check if file already exists in the new files list
      const isDuplicate = formData.files.some(existingFile => existingFile.name === file.name);

      if (isDuplicate) {
        toast.error(`File "${file.name}" is already selected`);
        return false;
      }
      if (!isValidType) {
        toast.error(`File ${file.name} has an invalid type`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`File ${file.name} is too large (max 50MB)`);
        return false;
      }
      return true;
    });

    // Only add files if validation passed
    if (validFiles.length > 0) {
      updateFormData({ files: [...formData.files, ...validFiles] });
    }
  }, [formData.files, formData.existingFiles.length, updateFormData]);

  const removeFile = useCallback((index: number) => {
    updateFormData({ files: formData.files.filter((_, i) => i !== index) });
  }, [formData.files, updateFormData]);

  const handleDeleteExistingFile = useCallback((fileId: string) => {
    const deletedFile = formData.existingFiles.find(f => f.id === fileId);
    updateFormData({
      existingFiles: formData.existingFiles.filter(f => f.id !== fileId),
      deletedFiles: deletedFile ? [...formData.deletedFiles, deletedFile] : formData.deletedFiles,
    });
  }, [formData.existingFiles, formData.deletedFiles, updateFormData]);

  // Helper function to check if there are any changes
  const hasAnyChanges = useCallback((): boolean => {
    if (!initialFormDataRef.current) return true;

    const initial = initialFormDataRef.current;
    const current = formData;

    // Check basic fields including visitDate
    if (initial.visitDate !== current.visitDate ||
      initial.status !== current.status ||
      initial.jobCompletionStatus !== current.jobCompletionStatus ||
      initial.markAsComp !== current.markAsComp ||
      initial.comments !== current.comments) {
      return true;
    }

    // Check if new files added or existing files deleted
    if (current.files.length > 0 || current.deletedFiles.length > 0) {
      return true;
    }

    // Check contractor trades changes
    const initialContractors = initial.contractorTrades.contractors;
    const currentContractors = current.contractorTrades.contractors;

    // Different number of contractors
    if (initialContractors.length !== currentContractors.length) {
      return true;
    }

    // Check each contractor
    for (const currentContractor of currentContractors) {
      const initialContractor = initialContractors.find(
        c => c.contractorId === currentContractor.contractorId
      );

      // New contractor
      if (!initialContractor) {
        return true;
      }

      // Different number of locals
      if (initialContractor.locals.length !== currentContractor.locals.length) {
        return true;
      }

      // Check each local
      for (const currentLocal of currentContractor.locals) {
        const initialLocal = initialContractor.locals.find(
          l => l.localUnionId === currentLocal.localUnionId
        );

        // New local
        if (!initialLocal) {
          return true;
        }

        // Different number of trades
        if (initialLocal.trades.length !== currentLocal.trades.length) {
          return true;
        }

        // Check each trade
        for (const currentTrade of currentLocal.trades) {
          const initialTrade = initialLocal.trades.find(
            t => t.tradeId === currentTrade.tradeId
          );

          // New trade or modified trade
          if (!initialTrade ||
            initialTrade.journeymen !== currentTrade.journeymen ||
            initialTrade.apprentices !== currentTrade.apprentices ||
            initialTrade.unionType !== currentTrade.unionType) {
            return true;
          }
        }
      }
    }

    return false;
  }, [formData]);

  // Submit Functions
  const handleSubmit = async () => {
    if (!validateCurrentStep() || !visitId) {
      return;
    }

    // Check if there are any actual changes
    if (!hasAnyChanges()) {
      toast.info('No changes detected');
      setShowConfirmDialog(false);
      return;
    }

    try {
      setSubmitting(true);
      setProcessingText('Updating jobsite visit...');
      const currentUser = authService.getUser();
      // Flag to check if jobsite details needs to be updated
      let updateJobsiteDetails = false;
      if (existingVisit) {
        // Check if current visit is the latest visit for the jobsie
        const latestJobsiteVisit = existingVisit.jobId.latestJobsiteVisitId;
        updateJobsiteDetails = existingVisit.id === latestJobsiteVisit;
      }

      if (!currentUser?.id) {
        toast.error('User authentication required');
        return;
      }

      // Step 1: Update basic visit info
      // Use the parent jobsite's CURRENT status to preserve it when updating the visit
      // The backend will override this to "completed" if jobCompletionStatus is "Project Completed"
      // This ensures that Inactive jobsites remain Inactive when editing visits
      const currentJobsiteStatus = jobsiteData?.jobDetails?.[0]?.status || formData.status;

      const visitUpdateData: JobsiteVisitUpdateDTO = {
        visitDate: formData.visitDate,
        status: currentJobsiteStatus as 'active' | 'inactive' | 'completed',
        jobCompletionStatus: formData.jobCompletionStatus,
        markAsComp: formData.markAsComp,
        userId: currentUser.id
      };

      const updatedVisit = await JobsiteVisitsService.updateJobsiteVisit(visitId, visitUpdateData);
      if (!updatedVisit) {
        toast.error('Failed to create jobsite visit workers');
        return;
      }

      // Update successful
      setHasEdits(false);

      // Step 2: Delete removed files (if any)
      if (formData.deletedFiles.length > 0) {
        setProcessingText('Removing deleted files...');
        await Promise.all(formData.deletedFiles.map(f => JobsiteVisitsService.deleteJobsiteVisitFile(f.id)));
      }

      // Step 3: Upload new files (if any)
      if (formData.files.length > 0) {
        await uploadFiles({
          files: formData.files,
          jobsiteVisitId: visitId,
          userId: currentUser.id,
          onProgress: setProcessingText,
        });
      }

      // Step 3: Update trades data using multi-contractor structure
      // Check if trades have changed (including removal of all contractors)
      const initialContractors = initialFormDataRef.current?.contractorTrades.contractors || [];
      const currentContractors = formData.contractorTrades.contractors;
      const tradesChanged = JSON.stringify(initialContractors) !== JSON.stringify(currentContractors);

      if (tradesChanged) {
        // Transform multi-contractor structure to backend format
        // Group by trade to create the jobTrade array
        const tradeMap = new Map<string, {
          tradeName: string;
          associations: Array<{
            contractorId: string;
            tradeType: string;
            noOfJourneymen: number;
            noOfApperntices: number;
            localUnionId: string;
            unionType: 'union' | 'clac' | 'merit' | 'other';
          }>;
        }>();

        // Flatten the nested structure: contractors → locals → trades
        formData.contractorTrades.contractors.forEach(contractor => {
          contractor.locals.forEach(local => {
            local.trades.forEach(trade => {
              if (!tradeMap.has(trade.tradeId)) {
                tradeMap.set(trade.tradeId, {
                  tradeName: trade.tradeName,
                  associations: []
                });
              }

              tradeMap.get(trade.tradeId)!.associations.push({
                contractorId: contractor.contractorId,
                tradeType: trade.tradeId,
                noOfJourneymen: trade.journeymen,
                noOfApperntices: trade.apprentices,
                localUnionId: local.localUnionId,
                unionType: trade.unionType
              });
            });
          });
        });

        // Convert map to jobTrade array
        const jobTrade: JobTradeVisitDTO[] = Array.from(tradeMap.entries()).map(([tradeId, data]) => {
          const totalWorkers = data.associations.reduce((sum, assoc) =>
            sum + assoc.noOfJourneymen + assoc.noOfApperntices, 0
          );

          // Calculate unionStatus based on the associations for this trade
          const unionTypes = data.associations.map(a => a.unionType);
          const hasUnion = unionTypes.includes('union');
          const hasNonUnion = unionTypes.some(t => t === 'clac' || t === 'merit' || t === 'other');

          let unionStatus: UnionStatus;
          if (hasUnion && hasNonUnion) {
            unionStatus = 'mixed'; // Mix of union and non-union
          } else if (hasUnion) {
            unionStatus = 'union'; // All union
          } else {
            // All are non-union subcategories (clac, merit, other)
            unionStatus = 'other';
          }

          return {
            tradesType: tradeId,
            unionStatus: unionStatus,
            isSignatory: false, // Not applicable for visits
            localUnionIds: [], // Locals are now stored per association
            numOfWorkers: totalWorkers,
            isUACanadaTrade: true,
            tradeContractAssociation: data.associations
          };
        });

        const tradesData: CreateJobsiteVisitsTradeDTO = {
          userId: currentUser.id,
          noOfTrades: jobTrade.length.toString(),
          jobTrade,
          jobsiteVisitId: visitId
        };

        await JobsiteVisitsService.updateJobsiteVisitTrades(visitId, tradesData);
      }

      // Step 4: Update comment if it changed
      // Update comment if there's an existing comment ID (allows deletion by clearing text)
      // OR if there's new comment text to add
      if (formData.existingCommentId || formData.comments.trim()) {
        // Only proceed if comment actually changed
        const commentChanged = initialFormDataRef.current?.comments !== formData.comments;

        if (commentChanged) {
          setProcessingText('Updating comment...');
          try {
            if (formData.existingCommentId) {
              // Update existing comment (allows deletion by clearing text)
              await JobsiteVisitsService.updateJobsiteVisitComment(
                formData.existingCommentId,
                formData.comments,
                currentUser.id
              );
            } else if (formData.comments.trim()) {
              // Create new comment only if there's text
              await JobsiteVisitsService.createJobsiteVisitComment({
                comment: formData.comments,
                userId: currentUser.id,
                jobsiteVisitId: visitId
              });
            }
          } catch (error) {
            console.error('Error updating comment:', error);
            toast.warn('Visit updated but comment failed to save');
          }
        }
      }

      setProcessingText('Jobsite visit updated successfully!');
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);
      toast.success('Jobsite visit updated successfully!');
      setTimeout(() => {
        navigate(`/jobsite-management/details/${jobId}`);
      }, 2000);
    } catch (error: any) {
      console.error('Error updating visit:', error);

      // Extract specific error message from backend if available
      const errorMessage = error?.response?.data?.message ||
                          error?.message ||
                          'Failed to update jobsite visit';

      toast.error(errorMessage);

      // If validation error related to contractors/trades, navigate to Step 2
      if (errorMessage.includes('local/trade') || errorMessage.includes('contractor')) {
        setCurrentStep(2);
      }
    } finally {
      setSubmitting(false);
      setProcessingText('');
    }
  };

  // Memoized computed values - don't validate immediately, only check if data is loaded
  const canProceed = useMemo(() => {
    // Don't validate if still loading or no existing visit data
    if (loading || !existingVisit) {
      return false;
    }
    // Only return true if we have basic required data without running validation
    return true;
  }, [loading, existingVisit]);

  const isLastStep = useMemo(() => {
    return currentStep === FORM_STEPS.length;
  }, [currentStep]);

  // Loading State
  if (loading) {
    return <JobsiteDetailsSkeleton />;
  }

  if (!existingVisit) {
    return (
      <div className="bg-gray-50 p-3 sm:p-6 min-h-screen">
        <div className="text-center">
          <p className="text-gray-500">Visit not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-3 sm:p-6 min-h-screen">
      <Navigation onBack={handleBack} />

      {/* Jobsite Info Header (Read-only) */}
      <div className="bg-white rounded-md shadow mb-4">
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Jobsite Name</p>
              <p className="font-medium">{jobsiteData?.jobDetails?.[0]?.jobSiteName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Contractor</p>
              <p className="font-medium">{jobsiteData?.jobDetails?.[0]?.generalContractor?.name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Current Status</p>
              <StatusPill status={jobsiteData?.jobDetails?.[0]?.status} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-md shadow">
        <div className="p-4 sm:p-6">
          {/* Step Progress Bar */}
          <div className="mb-8">
            <StepProgressBar
              steps={FORM_STEPS.map(step => step.title)}
              currentStep={currentStep}
            />
          </div>

          {/* Form Content */}
          <div className="mb-8">
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium text-gray-900">Visit Date</h2>
                <p className="text-sm text-gray-600">When did you visit this jobsite?</p>

                <div className="w-full max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Visit <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                    value={extractDateFromISO(formData.visitDate)}
                    max={getTodayDateString()}
                    onChange={(e) => {
                      const selectedDate = e.target.value;

                      // Validate: don't allow future dates
                      if (isDateInFuture(selectedDate)) {
                        toast.warn('Cannot select future dates');
                        return;
                      }

                      // Store date as ISO string at noon UTC to avoid timezone issues
                      const dateValue = dateToISOAtNoon(selectedDate);
                      updateFormData({
                        visitDate: dateValue
                      });
                    }}
                  />
                  {errors.visitDate && (
                    <p className="text-xs text-red-500 mt-1">{errors.visitDate}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Select the date you visited the jobsite. This date cannot be in the future.
                  </p>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <MultiContractorTradesStep
                contractors={contractors}
                selectedContractors={formData.contractorTrades.contractors}
                onChange={(contractors) =>
                  updateFormData({
                    contractorTrades: { contractors }
                  })
                }
                onContractorUpdate={(updatedContractor) => {
                  // Update contractors array with the new or edited contractor
                  setContractors((prev: any) => {
                    const index = prev.findIndex((c: any) => c.id === updatedContractor.id);
                    if (index >= 0) {
                      // Update existing contractor
                      const newContractors = [...prev];
                      newContractors[index] = updatedContractor;
                      return newContractors;
                    } else {
                      // Add new contractor
                      return [...prev, updatedContractor];
                    }
                  });
                }}
                errors={errors}
              />
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Files & Documents</h3>

                {/* Existing Files */}
                {formData.existingFiles.length > 0 && (
                  <div>
                    <h4 className="text-md font-medium text-gray-700 mb-2">Existing Files</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {formData.existingFiles.map((file, index) => (
                        <div key={index} className="relative border border-gray-200 rounded-lg p-3">
                          <button
                            type="button"
                            onClick={() => handleDeleteExistingFile(file.id)}
                            className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"
                            aria-label="Remove file"
                          >
                            <X className="w-5 h-5" />
                          </button>
                          <p className="text-sm font-medium text-gray-900 truncate pr-6">
                            {file.fileName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(file.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New File Upload */}
                <div>
                  <h4 className="text-md font-medium text-gray-700 mb-2">Add New Files</h4>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <div className="text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="file-upload" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            Drop files here or click to upload
                          </span>
                          <input
                            id="file-upload"
                            type="file"
                            className="sr-only"
                            multiple
                            accept="image/*,.pdf,.doc,.docx"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              handleFileUpload(files);
                              // Reset input to allow checking for duplicates on every selection
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <p className="mt-1 text-xs text-gray-500">
                          PNG, JPG, PDF up to 50MB each
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* New Files Preview */}
                  {formData.files.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium text-gray-900">New Files to Upload</h3>
                      <div className="space-y-2">
                        {formData.files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                            <div className="flex items-center">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                              <span className="text-sm text-gray-900">{file.name}</span>
                              <span className="ml-2 text-xs text-gray-500">
                                ({(file.size / 1024 / 1024).toFixed(1)} MB)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium text-gray-900">Job Status & Progress</h2>

                {/* Job Completion Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Completion Status <span className="text-red-500">*</span>
                  </label>
                  <SelectDropdown
                    options={completionSteps}
                    onSelect={(value: string) => updateFormData({ jobCompletionStatus: value })}
                    getValue={(option: any) => option.value}
                    getDisplayText={(option: any) => option.label}
                    selectedValue={formData.jobCompletionStatus}
                    placeholder="Select completion status"
                    disabled={formData.markAsComp}
                    testId="completion-status-select"
                  />
                  {errors.jobCompletionStatus && (
                    <p className="text-xs text-red-500 mt-1">{errors.jobCompletionStatus}</p>
                  )}
                </div>

                {/* Progress Bar */}
                {formData.jobCompletionStatus && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[16px] font-[700] text-black">Job Completion</span>
                      <span className="text-sm sm:text-md">
                        {getJobsiteCompletionPercentage(
                          completionSteps.find(s => s.value === formData.jobCompletionStatus)?.label || ''
                        )}%
                      </span>
                    </div>
                    <div className="relative">
                      <div className="w-full bg-gray-200 h-2 rounded-full">
                        <div
                          className="absolute left-0 top-0 bg-[#A42823] h-2 rounded-full"
                          style={{
                            width: `${getJobsiteCompletionPercentage(
                              completionSteps.find(s => s.value === formData.jobCompletionStatus)?.label || ''
                            )}%`
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Comments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Comments
                  </label>
                  <textarea
                    value={formData.comments}
                    onChange={(e) => updateFormData({ comments: e.target.value })}
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                    placeholder="Add any additional notes about this visit..."
                  />
                </div>
              </div>
            )}

            {currentStep === 5 && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium text-gray-900">Review & Submit</h2>

                {/* Review sections */}
                <div className="bg-gray-50 p-6 rounded-md space-y-6">
                  {/* Visit Date Summary */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Visit Date</h3>
                    <div className="text-sm text-gray-600">
                      <p>
                        <strong>Date of Visit:</strong>{' '}
                        {formData.visitDate ? new Date(formData.visitDate).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          timeZone: 'UTC'
                        }) : 'Not set'}
                        {initialFormDataRef.current && initialFormDataRef.current.visitDate !== formData.visitDate && (
                          <span className="ml-2 text-xs bg-yellow-600 text-white px-2 py-1 rounded">MODIFIED</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Contractor Trades Summary */}
                  {(formData.contractorTrades.contractors.length > 0 || (initialFormDataRef.current?.contractorTrades.contractors.length ?? 0) > 0) && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-3">Mechanical Contractors & Trades</h3>
                      <div className="text-sm text-gray-600 space-y-4">
                        {/* Show current contractors */}
                        {formData.contractorTrades.contractors.map((contractor, idx) => {
                          // Check if this contractor is new or modified
                          const initialContractor = initialFormDataRef.current?.contractorTrades.contractors.find(
                            c => c.contractorId === contractor.contractorId
                          );
                          const isNewContractor = !initialContractor;

                          return (
                            <div key={idx} className={`border-l-4 ${isNewContractor ? 'border-green-500' : 'border-gray-700'} pl-4 ${isNewContractor ? 'bg-green-50' : ''} ${isNewContractor ? 'p-2 rounded' : ''}`}>
                              <p className="font-semibold text-gray-900 mb-2">
                                {contractor.contractorName}
                                {isNewContractor && <span className="ml-2 text-xs font-normal bg-green-600 text-white px-2 py-1 rounded">NEW</span>}
                              </p>
                              {contractor.locals.map((local, localIdx) => {
                                // Check if this local is new
                                const initialLocal = initialContractor?.locals.find(
                                  l => l.localUnionId === local.localUnionId
                                );
                                const isNewLocal = !initialLocal;

                                return (
                                  <div key={localIdx} className={`ml-2 mb-3 ${isNewLocal ? 'bg-green-50 p-2 rounded' : ''}`}>
                                    <p className="font-medium text-gray-800">
                                      {local.localUnionName}
                                      {isNewLocal && <span className="ml-2 text-xs font-normal bg-green-600 text-white px-2 py-1 rounded">NEW</span>}
                                    </p>
                                    <div className="ml-4 mt-2 space-y-2">
                                      {local.trades.map((trade, tradeIdx) => {
                                        // Check if this trade is new or modified
                                        const initialTrade = initialLocal?.trades.find(
                                          t => t.tradeId === trade.tradeId
                                        );
                                        const isNewTrade = !initialTrade;
                                        const isModifiedTrade = initialTrade && (
                                          initialTrade.journeymen !== trade.journeymen ||
                                          initialTrade.apprentices !== trade.apprentices ||
                                          initialTrade.unionType !== trade.unionType
                                        );

                                        return (
                                          <div key={tradeIdx} className={`${isNewTrade ? 'bg-green-100' : isModifiedTrade ? 'bg-yellow-100' : 'bg-gray-100'} p-3 rounded`}>
                                            <p className="font-medium text-gray-900">
                                              {trade.tradeName}
                                              {isNewTrade && <span className="ml-2 text-xs font-normal bg-green-600 text-white px-2 py-1 rounded">NEW</span>}
                                              {isModifiedTrade && <span className="ml-2 text-xs font-normal bg-yellow-600 text-white px-2 py-1 rounded">MODIFIED</span>}
                                            </p>
                                            <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                                              <p className={initialTrade?.journeymen !== trade.journeymen ? 'font-bold' : ''}>
                                                <strong>Journeymen:</strong> {trade.journeymen}
                                                {initialTrade && initialTrade.journeymen !== trade.journeymen && (
                                                  <span className="ml-1 text-gray-500 line-through">({initialTrade.journeymen})</span>
                                                )}
                                              </p>
                                              <p className={initialTrade?.apprentices !== trade.apprentices ? 'font-bold' : ''}>
                                                <strong>Apprentices:</strong> {trade.apprentices}
                                                {initialTrade && initialTrade.apprentices !== trade.apprentices && (
                                                  <span className="ml-1 text-gray-500 line-through">({initialTrade.apprentices})</span>
                                                )}
                                              </p>
                                              <p className={initialTrade?.unionType !== trade.unionType ? 'font-bold' : ''}>
                                                <strong>Union Status:</strong> {formatUnionType(trade.unionType)}
                                                {initialTrade && initialTrade.unionType !== trade.unionType && (
                                                  <span className="ml-1 text-gray-500 line-through">({formatUnionType(initialTrade.unionType)})</span>
                                                )}
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}

                        {/* Show deleted contractors */}
                        {initialFormDataRef.current?.contractorTrades.contractors
                          .filter(initialContractor =>
                            !formData.contractorTrades.contractors.find(c => c.contractorId === initialContractor.contractorId)
                          )
                          .map((contractor, idx) => (
                            <div key={`deleted-${idx}`} className="border-l-4 border-red-500 pl-4 bg-red-50 p-2 rounded">
                              <p className="font-semibold text-gray-900 mb-2">
                                <span className="line-through">{contractor.contractorName}</span>
                                <span className="ml-2 text-xs font-normal bg-red-600 text-white px-2 py-1 rounded">DELETED</span>
                              </p>
                              {contractor.locals.map((local, localIdx) => (
                                <div key={localIdx} className="ml-2 mb-3 opacity-70">
                                  <p className="font-medium text-gray-800 line-through">
                                    {local.localUnionName}
                                  </p>
                                  <div className="ml-4 mt-2 space-y-2">
                                    {local.trades.map((trade, tradeIdx) => (
                                      <div key={tradeIdx} className="bg-red-100 p-3 rounded">
                                        <p className="font-medium text-gray-900 line-through">
                                          {trade.tradeName}
                                        </p>
                                        <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs line-through">
                                          <p>
                                            <strong>Journeymen:</strong> {trade.journeymen}
                                          </p>
                                          <p>
                                            <strong>Apprentices:</strong> {trade.apprentices}
                                          </p>
                                          <p>
                                            <strong>Union Status:</strong> {formatUnionType(trade.unionType)}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Files Summary */}
                  {(formData.existingFiles.length > 0 || formData.files.length > 0 || formData.deletedFiles.length > 0) && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">Files</h3>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Total Files:</strong> {formData.existingFiles.length + formData.files.length}
                      </p>
                      <div className="space-y-2">
                        {/* Existing Files */}
                        {formData.existingFiles.map((file) => (
                          <div key={file.id} className="bg-gray-100 p-2 rounded text-sm">
                            <p className="text-gray-900">{file.fileName}</p>
                          </div>
                        ))}
                        {/* Deleted Files */}
                        {formData.deletedFiles.map((file) => (
                          <div key={file.id} className="border-l-4 border-red-500 pl-4 bg-red-50 p-2 rounded text-sm">
                            <p className="text-gray-900">
                              <span className="line-through">{file.fileName}</span>
                              <span className="ml-2 text-xs font-normal bg-red-600 text-white px-2 py-1 rounded">DELETED</span>
                            </p>
                          </div>
                        ))}
                        {/* New Files */}
                        {formData.files.map((file, idx) => (
                          <div key={idx} className="bg-green-100 p-2 rounded text-sm">
                            <p className="text-gray-900">
                              {file.name}
                              <span className="ml-2 text-xs font-normal bg-green-600 text-white px-2 py-1 rounded">NEW</span>
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status Summary */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Job Status</h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        <strong>Completion Status:</strong> {
                          completionSteps.find(s => s.value === formData.jobCompletionStatus)?.label || 'Not selected'
                        }
                        {initialFormDataRef.current && initialFormDataRef.current.jobCompletionStatus !== formData.jobCompletionStatus && (
                          <>
                            <span className="ml-2 text-xs bg-yellow-600 text-white px-2 py-1 rounded">MODIFIED</span>
                            {initialFormDataRef.current.jobCompletionStatus && (
                              <span className="ml-2 text-gray-500 line-through">
                                ({completionSteps.find(s => s.value === initialFormDataRef.current?.jobCompletionStatus)?.label})
                              </span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Comments Summary */}
                  {(() => {
                    const isModified = initialFormDataRef.current && initialFormDataRef.current.comments !== formData.comments;
                    const hadComment = initialFormDataRef.current?.comments;
                    const hasComment = formData.comments;

                    // Determine the status label
                    let statusLabel = '';
                    let statusClass = '';
                    if (isModified) {
                      if (!hadComment && hasComment) {
                        statusLabel = 'NEW';
                        statusClass = 'bg-green-600';
                      } else if (hadComment && hasComment) {
                        statusLabel = 'MODIFIED';
                        statusClass = 'bg-yellow-600';
                      } else if (hadComment && !hasComment) {
                        statusLabel = 'DELETED';
                        statusClass = 'bg-red-600';
                      }
                    }

                    // Show if there's a comment OR if there was a comment that got deleted
                    if (hasComment || (hadComment && !hasComment)) {
                      return (
                        <div>
                          <h3 className="font-medium text-gray-900 mb-2">
                            Comments
                            {statusLabel && (
                              <span className={`ml-2 text-xs ${statusClass} text-white px-2 py-1 rounded`}>
                                {statusLabel}
                              </span>
                            )}
                          </h3>
                          {hasComment ? (
                            <p className="text-sm text-gray-600">{formData.comments}</p>
                          ) : (
                            <p className="text-sm text-gray-500 italic line-through">{initialFormDataRef?.current?.comments}</p>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between">
            <button
              type="button"
              onClick={handlePrevStep}
              disabled={currentStep === 1}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <div className="flex gap-3">
              {!isLastStep ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  disabled={!canProceed}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowConfirmDialog(true)}
                  disabled={!canProceed || submitting}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Updating...' : 'Update Visit'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        isOpen={showConfirmDialog}
        onClose={() => !submitting && setShowConfirmDialog(false)}
        width="w-full sm:w-[500px]"
        height="auto"
        disableBackdropClick={true}
      >
        <div className="text-center p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {submitting ? 'Updating Jobsite Visit' : 'Confirm Update'}
          </h3>
          {submitting ? (
            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
              <p className="text-sm text-gray-600">{processingText}</p>
              <p className="text-xs text-gray-500 mt-2">Please wait, do not close this window...</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to update this jobsite visit? This action cannot be undone.
            </p>
          )}
          <div className="flex justify-center gap-3">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowConfirmDialog(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Processing...' : 'Update Visit'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        width="w-full sm:w-[400px]"
        height="auto"
        title="Visit Updated Successfully"
      >
        <div className="text-center p-6">
          <div className="w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <Plus className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Visit Updated Successfully!
          </h3>
          <p className="text-sm text-gray-500">
            Redirecting to jobsite details...
          </p>
        </div>
      </Dialog>
    </div>
  );
};

export default EditJobsiteVisit;